import { ColumnDef, flexRender, getCoreRowModel, useReactTable, getFilteredRowModel } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { useRef, useState } from 'react'

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { TableConfig } from './types-v4'
import { Button } from '@/components/shadcn/button'
import { IconPlusOutline } from '@/components/icons'
import { InferFormValues, JsonFormSchemaV2 } from '../FormComponent/types'
import { SheetWithForm } from '@/components/ui/SheetWithForm'
import { SearchInput } from './SearchInput'
import { TileFilter } from '../TileFilter'
import { ComponentTitle } from '../ComponentTitle'

interface TableContentProps<TData, TValue> {
  tableConfig: TableConfig
  columns: ColumnDef<TData, TValue>[]
  data: TData[]
  formSchema?: JsonFormSchemaV2
  tileFilterSchema?: JsonFormSchemaV2
  filterChangeHandler?: (data: InferFormValues<JsonFormSchemaV2>) => void
}

export function TableContent<TData, TValue>({
  tableConfig,
  columns,
  data,
  formSchema,
  tileFilterSchema,
  filterChangeHandler,
}: TableContentProps<TData, TValue>) {
  // Create a reference to the table body
  const tableBodyRef = useRef<HTMLTableSectionElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)
  const { title, description } = tableConfig
  const [globalFilter, setGlobalFilter] = useState('')

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: 'includesString',
    state: {
      globalFilter,
    },
  })

  // Set up the virtualizer
  const { rows } = table.getRowModel()
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollAreaRef.current,
    estimateSize: () => 45, // Estimate row height (adjust based on your actual row height)
    overscan: 5, // Number of items to render before/after the visible area
  })

  const {
    permissions: { canCreate },
  } = tableConfig

  // Get virtual rows
  const virtualRows = rowVirtualizer.getVirtualItems()
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0
  const paddingBottom =
    virtualRows.length > 0 ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end : 0

  return (
    <div className="flex flex-col gap-y-6">
      <div className="flex w-full flex-wrap items-center justify-between gap-4">
        <ComponentTitle title={title} description={description} />
        <TileFilter schema={tileFilterSchema} submitHandler={filterChangeHandler} className="ml-auto" />
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
        {canCreate && formSchema && (
          <SheetWithForm
            triggerTooltip="Add an entry"
            trigger={
              <Button size="icon" variant="outline" className="size-8 rounded-full">
                <IconPlusOutline className="size-4" />
              </Button>
            }
            schema={formSchema}
            onSubmit={(data: InferFormValues<typeof formSchema>) => {
              console.log(data)
            }}
          />
        )}
      </div>
      <div className="rounded-md border">
        <div className="overflow-hidden">
          {/* Sticky Header */}
          <div className="relative">
            <Table className="w-full table-fixed border-collapse">
              <colgroup>
                {table.getAllColumns().map((column) => (
                  <col
                    key={column.id}
                    style={{ width: column.columnDef.size ? `${column.columnDef.size}px` : `${100 / columns.length}%` }}
                  />
                ))}
              </colgroup>
              <TableHeader className="sticky top-0 bg-background">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="shadow-[0_1px_2px_0_rgba(0,0,0,0.1)]">
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
          </div>

          {/* Scrollable Body */}
          <ScrollArea ref={scrollAreaRef} className="h-[600px]" scrollHideDelay={0}>
            <Table className="w-full table-fixed border-collapse">
              <colgroup>
                {table.getAllColumns().map((column) => (
                  <col
                    key={column.id}
                    style={{ width: column.columnDef.size ? `${column.columnDef.size}px` : `${100 / columns.length}%` }}
                  />
                ))}
              </colgroup>
              <TableBody ref={tableBodyRef}>
                {rows.length > 0 ? (
                  <>
                    {paddingTop > 0 && (
                      <tr>
                        <td style={{ height: `${paddingTop}px` }} colSpan={columns.length} />
                      </tr>
                    )}

                    {virtualRows.map((virtualRow) => {
                      const row = rows[virtualRow.index]
                      return (
                        <TableRow
                          key={row.id}
                          data-state={row.getIsSelected() && 'selected'}
                          data-index={virtualRow.index}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell key={cell.id}>
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}

                    {paddingBottom > 0 && (
                      <tr>
                        <td style={{ height: `${paddingBottom}px` }} colSpan={columns.length} />
                      </tr>
                    )}
                  </>
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </div>
      </div>
    </div>
  )
}
